import React from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import { useDarkMode } from "../../../hooks/useDarkMode";
import { Helmet } from "react-helmet";

export default function TermsConditions() {
  const { isDarkMode } = useDarkMode();
  const navigate = useNavigate();
  const location = useLocation();

  const isInDashboard = location.pathname.startsWith("/dashboard");
  const paddingClass = isInDashboard ? "py-8" : "pt-24 pb-14";

  const pageBg = isDarkMode ? "bg-gray-950" : "bg-gray-50";
  const cardBg = isDarkMode ? "bg-gray-900" : "bg-white";
  const heading = isDarkMode ? "text-white" : "text-gray-900";
  const text = isDarkMode ? "text-gray-300" : "text-gray-700";
  const subText = isDarkMode ? "text-gray-400" : "text-gray-600";

  return (
    <>
      {/* ===== SEO HELMET ===== */}
      <Helmet>
        <title>Terms & Conditions | Seo Up Rank AI SEO Platform</title>

        <meta
          name="description"
          content="Read the Terms and Conditions of Seo Up Rank, an AI powered SEO automation platform providing SEO audits, keyword research, ranking insights and website optimization tools."
        />

        <meta
          name="keywords"
          content="AI SEO tools, SEO automation platform, keyword research tool, website audit tool, Seo Up Rank"
        />

        <link rel="canonical" href="https://novarsis.ai/terms-conditions" />
        <link rel="home" href="https://novarsis.ai/" />

        <meta property="og:title" content="Terms & Conditions | Seo Up Rank" />
        <meta
          property="og:description"
          content="Terms and Conditions for using Seo Up Rank AI powered SEO tools and website optimization services."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://novarsis.ai/terms-conditions" />

        <meta name="robots" content="index, follow" />
      </Helmet>

      <section
        className={`relative min-h-screen w-full overflow-x-hidden px-4 sm:px-6 ${paddingClass} ${pageBg}`}
      >
        {/* ===== SEO HIDDEN TEXT ===== */}
        <p className="sr-only">
          Seo Up Rank is an AI powered SEO automation platform that helps users
          improve search engine rankings, perform technical SEO audits,
          analyze keywords, and optimize websites for better organic traffic.
        </p>

        {/* ===== DECORATIVE BACKGROUND ===== */}
        <div className="absolute -top-24 -left-24 w-56 sm:w-72 h-56 sm:h-72 bg-[#008300]/10 rounded-full blur-3xl" />
        <div className="absolute top-1/3 -right-24 w-64 sm:w-80 h-64 sm:h-80 bg-[#008300]/15 rounded-full blur-3xl" />
        <div className="absolute bottom-24 left-1/4 w-52 sm:w-64 h-52 sm:h-64 bg-[#008300]/10 rounded-full blur-3xl" />

        {/* ===== CONTENT CARD ===== */}
        <div
          className={`relative z-10 max-w-4xl xl:max-w-5xl mx-auto rounded-2xl sm:rounded-3xl shadow-2xl ${cardBg}
          p-5 sm:p-8 md:p-10`}
        >
          {/* BACK BUTTON */}
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 mb-6 text-[#008300] hover:opacity-80 transition text-sm sm:text-base"
          >
            <FaArrowLeft />
            <span className="font-medium">Back</span>
          </button>

          {/* HEADER */}
          <h1
            className={`text-2xl sm:text-3xl md:text-4xl font-extrabold leading-tight ${heading}`}
          >
            Terms & Conditions
          </h1>

          <p className={`mt-2 text-xs sm:text-sm ${subText}`}>
            Last updated: {new Date().toLocaleDateString()}
          </p>

          {/* INTRO */}
          <div className="mt-6 sm:mt-8">
            <p className={`${text} leading-relaxed text-sm sm:text-base`}>
              These Terms & Conditions govern your access to and use of{" "}
              <strong>Seo Up Rank</strong>, an AI-powered SEO automation and
              analytics platform.

              You can also visit our official website{" "}
              <a
                href="https://novarsis.ai/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#008300] font-semibold hover:underline"
              >
                https://novarsis.ai
              </a>{" "}
              to learn more about our AI SEO tools and website optimization
              services.

              You may also review our{" "}
              <Link
                to="/privacy-policy"
                className="text-[#008300] font-medium hover:underline"
              >
                Privacy Policy
              </Link>{" "}
              and{" "}
              <Link
                to="/about"
                className="text-[#008300] font-medium hover:underline"
              >
                About Page
              </Link>
              .
            </p>
          </div>

          {/* TERMS SECTIONS */}
          {[
            {
              title: "1. Acceptance of Terms",
              body: "By accessing or using Seo Up Rank, you confirm that you have read, understood, and agree to be bound by these Terms.",
            },
            {
              title: "2. Description of Service",
              body: "Seo Up Rank provides AI driven SEO analysis, website audits, keyword insights, performance reports, and optimization recommendations.",
            },
            {
              title: "3. Accounts & Security",
              list: [
                "Maintain account confidentiality",
                "Provide accurate updated information",
                "Report unauthorized access immediately",
              ],
            },
            {
              title: "4. User Responsibilities",
              list: [
                "No misuse of SEO tools",
                "No unauthorized access attempts",
                "No illegal or harmful submissions",
              ],
            },
            {
              title: "5. Website Access & SEO Data",
              body: "You confirm legal rights to analyze submitted websites. SEO results and recommendations are informational only.",
            },
            {
              title: "6. Subscriptions & Billing",
              list: [
                "Paid plans may apply",
                "Pricing shown at checkout",
                "Auto renew unless cancelled",
              ],
            },
            {
              title: "7. Intellectual Property",
              body: "All software, algorithms, content, and branding belong to Seo Up Rank or its licensors.",
            },
            {
              title: "8. Third Party Services",
              body: "Third party integrations may be used. We are not responsible for their policies or actions.",
            },
            {
              title: "9. Disclaimers",
              body: "THE SERVICE IS PROVIDED AS IS WITHOUT WARRANTIES. SEO RESULTS ARE NOT GUARANTEED.",
            },
            {
              title: "10. Limitation of Liability",
              body: "Seo Up Rank shall not be liable for indirect or consequential damages.",
            },
            {
              title: "11. Termination",
              body: "Access may be suspended or terminated upon violation of Terms.",
            },
            {
              title: "12. Governing Law",
              body: "These Terms are governed by applicable local laws.",
            },
            {
              title: "13. Changes to Terms",
              body: "We may update Terms periodically. Continued use implies acceptance.",
            },
          ].map((item, i) => (
            <div key={i} className="mt-8 sm:mt-10">
              <h2
                className={`text-lg sm:text-xl md:text-2xl font-bold ${heading}`}
              >
                {item.title}
              </h2>

              {item.body && (
                <p
                  className={`mt-3 sm:mt-4 leading-relaxed ${text} text-sm sm:text-base`}
                >
                  {item.body}
                </p>
              )}

              {item.list && (
                <ul
                  className={`list-disc pl-6 mt-4 space-y-2 ${text} text-sm sm:text-base`}
                >
                  {item.list.map((li, idx) => (
                    <li key={idx}>{li}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}

          {/* CONTACT */}
          <div className="mt-10 sm:mt-12 p-5 sm:p-6 rounded-xl sm:rounded-2xl bg-[#008300]/10">
            <h3 className={`text-lg sm:text-xl font-bold ${heading}`}>
              Contact Us
            </h3>

            <p className="sr-only">
              Contact Seo Up Rank support for help related to AI SEO tools,
              keyword research, SEO audits, and website optimization services.
            </p>

            <p className={`mt-3 ${text} text-sm sm:text-base`}>
              Email:{" "}
              <a
                href="mailto:contact@novarsistech.com"
                className="text-[#008300] font-medium hover:underline break-all"
              >
                info@novarsistech.com
              </a>
            </p>

            <p className={`mt-1 ${text} text-sm sm:text-base`}>
              Response time: 2–3 business days
            </p>
          </div>
        </div>
      </section>
    </>
  );
}